import axios from 'axios';
import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';
import { environment } from './environment';
import { msalConfig } from './authConfig';

/**
 * Helper to select an active account from available accounts
 * Mirrors the logic in App.tsx selectActiveAccount()
 */
function ensureActiveAccount(msalInstance: IPublicClientApplication): AccountInfo | null {
  // If there's already an active account, return it
  let account = msalInstance.getActiveAccount();
  if (account) {
    return account;
  }

  // No active account - try to select one from available accounts
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return null;
  }

  // Try to find saved account from localStorage
  const ACTIVE_ACCOUNT_KEY = 'msalHomeAccountId';
  const savedHomeId = localStorage.getItem(ACTIVE_ACCOUNT_KEY);

  // Get tenant ID from authority
  const authority = msalConfig.auth?.authority || '';
  const parts = authority.split('/').filter(Boolean);
  const tenantId = parts[parts.length - 1]?.toLowerCase() || '';

  // Priority: saved account > tenant account > first account
  const savedAccount = savedHomeId
    ? accounts.find((acc) => acc.homeAccountId === savedHomeId)
    : undefined;

  const tenantAccount = tenantId
    ? accounts.find((acc) => acc.tenantId?.toLowerCase() === tenantId)
    : undefined;

  account = savedAccount || tenantAccount || accounts[0];

  if (account) {
    msalInstance.setActiveAccount(account);
  }

  return account;
}

/**
 * Check if MSAL has valid data in localStorage
 */
function hasMsalStorageData(): boolean {
  const keys = Object.keys(localStorage);
  return keys.some(
    (key) =>
      key.startsWith('msal.') &&
      (key.includes('.account.') || key.includes('.idtoken.') || key.includes('.accesstoken.'))
  );
}

/**
 * Force MSAL to recover accounts from localStorage
 * This creates a fresh instance that properly loads from cache
 */
async function forceRecoverMsalAccounts(): Promise<IPublicClientApplication | null> {
  if (!hasMsalStorageData()) {
    return null;
  }

  try {
    const freshInstance = new PublicClientApplication(msalConfig);
    await freshInstance.initialize();

    const accounts = freshInstance.getAllAccounts();

    if (accounts.length > 0) {
      type WindowWithMsal = { msalInstance?: IPublicClientApplication; msalInitialized?: boolean };
      (window as WindowWithMsal).msalInstance = freshInstance;
      (window as WindowWithMsal).msalInitialized = true;
      ensureActiveAccount(freshInstance);
      return freshInstance;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Create axios instance with base configuration
 */
export const apiClient = axios.create({
  baseURL: environment.apiUrl,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Response interceptor to handle authentication and authorization errors
 * - 401: Token expired, attempt refresh and retry, logout on persistent failure
 * - 403: Forbidden, redirect to access denied page
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

      try {
        const refreshedHeaders = await getAuthHeaders();
        originalRequest.headers = {
          ...originalRequest.headers,
          ...refreshedHeaders,
        };
        return apiClient(originalRequest);
      } catch (refreshError) {
        if (originalRequest._retryCount >= 2) {
          const msalInstance = (window as { msalInstance?: IPublicClientApplication }).msalInstance;

          if (msalInstance) {
            const showToastFn = (window as { showSessionExpiredToast?: () => void })
              .showSessionExpiredToast;
            if (showToastFn) {
              showToastFn();
            }

            await new Promise((resolve) => setTimeout(resolve, 500));

            try {
              await msalInstance.logoutRedirect({
                postLogoutRedirectUri: '/',
              });
              return new Promise(() => {});
            } catch {
              window.location.href = '/';
              return new Promise(() => {});
            }
          }

          return Promise.reject(error);
        } else {
          return Promise.reject(error);
        }
      }
    }

    // Handle 403 errors (forbidden - insufficient permissions)
    // Don't redirect — let ProtectedRoute handle page-level access control.
    // API 403s are data-level issues that pages/components should handle gracefully.
    if (error.response?.status === 403) {
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to get authorization headers
 * Integrates with MSAL authentication system
 * Handles automatic token refresh when tokens expire
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    type WindowWithMsal = {
      msalInstance?: IPublicClientApplication;
      msalInitialized?: boolean;
    };

    let msalInstance = (window as WindowWithMsal).msalInstance;
    let msalInitialized = (window as WindowWithMsal).msalInitialized;

    const hasStoredUserData =
      localStorage.getItem('currentUserRoles') !== null ||
      localStorage.getItem('msalHomeAccountId') !== null;

    const hasAccounts = (msalInstance?.getAllAccounts?.()?.length ?? 0) > 0;
    const needsWait = !msalInstance || !msalInitialized || (hasStoredUserData && !hasAccounts);

    if (needsWait) {
      let retries = 0;
      const maxRetries = 10;
      const baseDelay = 50;
      let recoveryAttempted = false;

      while (retries < maxRetries) {
        msalInstance = (window as WindowWithMsal).msalInstance;
        msalInitialized = (window as WindowWithMsal).msalInitialized;
        const currentHasAccounts = (msalInstance?.getAllAccounts?.()?.length ?? 0) > 0;

        if (msalInstance && msalInitialized && currentHasAccounts) {
          break;
        }

        if (!hasStoredUserData && msalInstance && msalInitialized) {
          break;
        }

        // If instance exists but has no accounts, attempt recovery from localStorage
        if (
          msalInstance &&
          msalInitialized &&
          !currentHasAccounts &&
          hasStoredUserData &&
          !recoveryAttempted &&
          retries >= 3
        ) {
          recoveryAttempted = true;
          const recoveredInstance = await forceRecoverMsalAccounts();
          if (recoveredInstance) {
            msalInstance = recoveredInstance;
            msalInitialized = true;
            break;
          }
        }

        await new Promise((resolve) => setTimeout(resolve, baseDelay * (retries + 1)));
        retries++;
      }

      msalInstance = (window as WindowWithMsal).msalInstance;
      msalInitialized = (window as WindowWithMsal).msalInitialized;
    }

    if (msalInstance && msalInitialized) {
      let account = ensureActiveAccount(msalInstance);

      // If still no account but we have MSAL storage, try one more recovery
      if (!account && hasMsalStorageData()) {
        const recoveredInstance = await forceRecoverMsalAccounts();
        if (recoveredInstance) {
          msalInstance = recoveredInstance;
          account = ensureActiveAccount(recoveredInstance);
        }
      }

      if (account) {
        const tokenRequest = {
          scopes: [environment.msalConfig.apiScope],
          account: account,
        };

        try {
          const response = await msalInstance.acquireTokenSilent(tokenRequest);
          headers['Authorization'] = `Bearer ${response.accessToken}`;
        } catch (silentError) {
          const errorName = (silentError as { name?: string })?.name;
          const errorMessage = (silentError as { message?: string })?.message || '';

          if (
            errorName === 'InteractionRequiredAuthError' ||
            errorMessage.includes('interaction_required') ||
            errorMessage.includes('consent_required') ||
            errorMessage.includes('login_required')
          ) {
            await msalInstance.acquireTokenRedirect(tokenRequest);
          }
        }
      }
    }
  } catch {
    // Continue without authorization header
  }

  return headers;
}

/**
 * Axios instance configured for API calls with automatic token handling
 */
export class ApiClient {
  private static async networkGet<T>(url: string, config?: Record<string, unknown>): Promise<T> {
    const headers = await getAuthHeaders();
    const configHeaders = (config?.headers as Record<string, string>) || {};
    const response = await apiClient.get(url, {
      ...config,
      headers: { ...headers, ...configHeaders },
    });
    return response.data as T;
  }

  static async get<T>(url: string, config?: Record<string, unknown>): Promise<T> {
    return this.networkGet<T>(url, config);
  }

  static async post<T>(url: string, data?: unknown, config?: Record<string, unknown>): Promise<T> {
    const headers = await getAuthHeaders();
    const configHeaders = (config?.headers as Record<string, string>) || {};
    const response = await apiClient.post(url, data, {
      ...config,
      headers: { ...headers, ...configHeaders },
    });

    return response.data as T;
  }

  static async put<T>(url: string, data?: unknown, config?: Record<string, unknown>): Promise<T> {
    const headers = await getAuthHeaders();
    const configHeaders = (config?.headers as Record<string, string>) || {};
    const response = await apiClient.put(url, data, {
      ...config,
      headers: { ...headers, ...configHeaders },
    });

    return response.data as T;
  }

  static async delete<T>(url: string, config?: Record<string, unknown>): Promise<T> {
    const headers = await getAuthHeaders();
    const configHeaders = (config?.headers as Record<string, string>) || {};
    const response = await apiClient.delete(url, {
      ...config,
      headers: { ...headers, ...configHeaders },
    });

    return response.data as T;
  }

  static async download(url: string, config?: Record<string, unknown>): Promise<Blob> {
    const headers = await getAuthHeaders();
    const configHeaders = (config?.headers as Record<string, string>) || {};
    const response = await apiClient.get(url, {
      ...config,
      headers: { ...headers, ...configHeaders },
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  static async downloadWithHeaders(
    url: string,
    config?: Record<string, unknown>
  ): Promise<{ data: Blob; headers: Record<string, string> }> {
    const headers = await getAuthHeaders();
    const configHeaders = (config?.headers as Record<string, string>) || {};
    const response = await apiClient.get(url, {
      ...config,
      headers: { ...headers, ...configHeaders },
      responseType: 'blob',
    });
    return {
      data: response.data as Blob,
      headers: response.headers as Record<string, string>,
    };
  }
}

export default apiClient;
